import React, { useState } from 'react';
import { Trash2 } from 'lucide-react';

import { cn } from '@/lib/utils';
import { useT } from '@/contexts/I18nContext';
import { spliceLine, serializeFoodStatus, type FoodStatusLine } from '../constructor/lineModel';
import { AutoSegSeeder, RackChart, type AutoSeg } from './RackChart';
import { Tooltip } from '@/components/ui/tooltip';

const BUCKETS = [10, 20, 30, 40, 50, 60, 70, 80, 90, 100];

type RackSeg = AutoSeg;

/**
 * F1 — Выкладка status line: one annotatable chart per rack of the type,
 * segments annotated independently per rack. Dragging computes `missingPct`
 * from that rack's own fill-series (RackChart); the aggregate (shelf count /
 * average shortage / duration) is recomputed on every change and pushed back
 * into `body_html` via the existing `serializeFoodStatus` — same text-write
 * path the V2 constructor form uses, so this and the "circle" form stay
 * interchangeable on the same line.
 */
export function FoodStatusChartBlock({
  line,
  racks,
  zoneId,
  day,
  value,
  onChange,
  disabled,
}: {
  line: FoodStatusLine;
  racks: { id: number; name: string }[];
  zoneId: string;
  day: string;
  value: string;
  onChange: (bodyHtml: string) => void;
  disabled?: boolean;
}) {
  const { t } = useT();
  const [segByRack, setSegByRack] = useState<Record<number, RackSeg[]>>({});
  const [selByRack, setSelByRack] = useState<Record<number, string | null>>({});
  // item A: which racks' auto-detected segments have already been seeded —
  // tracked separately from `segByRack` so a user deleting every auto
  // segment (a legitimate "actually fine, no problem here" correction)
  // doesn't read as "not seeded yet" and bring them back.
  const [seededRacks, setSeededRacks] = useState<Set<number>>(new Set());
  const seedRack = (rackId: number, segs: RackSeg[]) => {
    setSeededRacks((prev) => new Set(prev).add(rackId));
    if (segs.length) setSegByRack((prev) => ({ ...prev, [rackId]: segs }));
  };

  // The circle is derived inside serializeFoodStatus from the recomputed
  // avg/duration (Alexey 2026-09-08) — the old severity picker, which kept a
  // stale colour over freshly edited numbers, is gone.
  const pushAggregate = (next: Record<number, RackSeg[]>) => {
    const all = Object.values(next).flat();
    const shelfCount = Object.values(next).filter((arr) => arr.length > 0).length;
    const avgShortage = all.length
      ? Math.round(all.reduce((a, s) => a + s.missingPct, 0) / all.length)
      : 0;
    const durationHours =
      Math.round((all.reduce((a, s) => a + (s.t_end - s.t_start), 0) / 3_600_000) * 10) / 10;
    onChange(
      spliceLine(
        value,
        line,
        serializeFoodStatus({
          ...line.fields,
          ok: all.length === 0,
          shelfCount,
          avgShortage,
          durationHours,
        }),
      ),
    );
  };

  const updateRack = (rackId: number, fn: (arr: RackSeg[]) => RackSeg[]) => {
    setSegByRack((prev) => {
      const next = { ...prev, [rackId]: fn(prev[rackId] ?? []) };
      pushAggregate(next);
      return next;
    });
  };

  return (
    <div className="flex flex-col gap-3">
      {racks.map((rack) =>
        seededRacks.has(rack.id) ? null : (
          <AutoSegSeeder
            key={`seed-${rack.id}`}
            zoneId={zoneId}
            day={day}
            rackId={rack.id}
            alreadySeeded={seededRacks.has(rack.id)}
            onSeed={seedRack}
          />
        ),
      )}
      {/* item 2: each rack gets its OWN full width — a 2-up grid squeezed two
          charts (and their ≥10-photo strips) into half-width each. */}
      <div className="flex flex-col gap-3">
        {racks.map((rack) => {
          const segs = segByRack[rack.id] ?? [];
          const selId = selByRack[rack.id] ?? null;
          const sel = segs.find((s) => s.id === selId) ?? null;
          return (
            <RackChart
              key={rack.id}
              zoneId={zoneId}
              day={day}
              rackId={rack.id}
              rackName={rack.name}
              annotateCreates="segment"
              segments={segs}
              selectedId={selId}
              onSelect={(id) => setSelByRack((prev) => ({ ...prev, [rack.id]: id }))}
              onSegmentCreate={(seg, missingPct) => {
                updateRack(rack.id, (arr) => [...arr, { ...seg, missingPct }]);
                setSelByRack((prev) => ({ ...prev, [rack.id]: seg.id }));
              }}
              onSegmentChange={(seg, missingPct) =>
                updateRack(rack.id, (arr) =>
                  arr.map((s) => (s.id === seg.id ? { ...seg, missingPct } : s)),
                )
              }
              addSegmentLabel={t('reportValidation.interactive.addProblem')}
              readout={
                sel ? (
                  <div className="flex flex-wrap items-center gap-2 rounded-md border border-border bg-muted/30 p-2 text-xs">
                    <span className="text-muted-foreground">{t('reportValidation.form.avgShortage')}:</span>
                    {BUCKETS.map((b) => (
                      <button
                        key={b}
                        type="button"
                        disabled={disabled}
                        onClick={() =>
                          updateRack(rack.id, (arr) =>
                            arr.map((s) => (s.id === sel.id ? { ...s, missingPct: b } : s)),
                          )
                        }
                        className={cn(
                          'rounded-md border border-border px-1.5 py-0.5',
                          sel.missingPct === b ? 'ring-2 ring-primary' : 'opacity-70 hover:opacity-100',
                        )}
                      >
                        {b}%
                      </button>
                    ))}
                    <Tooltip content={t('reportValidation.interactive.removeSegment')}><button
                      type="button"
                      disabled={disabled}
                      aria-label={t('reportValidation.interactive.removeSegment')}
                      onClick={() => {
                        updateRack(rack.id, (arr) => arr.filter((s) => s.id !== sel.id));
                        setSelByRack((prev) => ({ ...prev, [rack.id]: null }));
                      }}
                      className="ml-auto rounded-md border border-border p-1 text-destructive hover:bg-destructive/10"
                    >
                      <Trash2 className="size-3.5" />
                    </button></Tooltip>
                  </div>
                ) : undefined
              }
            />
          );
        })}
      </div>
    </div>
  );
}
